import React from 'react';
import RiskOverview from '../../../Pages/RiskOverview';
import {
    RISK_OVERVIEW_RESOURCE_STATES,
    createRiskOverviewResource,
    readRiskOverviewResource,
} from '../riskOverviewResource';

export default function DashboardOverviewSection({
    expectedActualScopeKey,
    expectedSelectedScopeKey,
    riskOverviewTopPanels,
    calculationsLoading,
    matricesLoading,
    calculations,
    optimizedRiskMetrics,
    groupRiskMetrics,
    optimizedGroupRiskMetrics,
    groupRisksLoading,
    optimizedGroupRiskMetricsLoading,
    optimalPortfolioSummaryLoaded,
    optimalPortfolioSummary,
    selectedFactor,
    handleSelectFactor,
    sedolsForFactor,
    tickersLoading,
    tickersError,
    combinedHoldings,
    industries,
    watchlistAttributeSnapshot,
    priceRefreshLoading,
    priceSyncControl,
    onPriceSync,
    betaSourceTransitionLoading,
    coreLoadVisualLoading,
    actualPortfolioIsReady,
    positionsLoading,
    holdingsLoading,
    riskFactorsHeaderActions,
    aum = null,
    aumLoading = false,
    handleOpenPortfolioImports,
    showPortfolioImportGuide = false,
    onDismissPortfolioImportGuide = null,
    onOpenPortfolioEditor,
    onOpenPositionSizing,
}) {
    const hasActualPortfolioPositions = Array.isArray(combinedHoldings)
        && combinedHoldings.length > 0;
    const hasExpectedActualScope = expectedActualScopeKey != null;
    const hasExpectedSelectedScope = expectedSelectedScopeKey != null;

    const isActualUnresolved = actualPortfolioIsReady === null;
    const isActualScopePending = actualPortfolioIsReady !== undefined
        && actualPortfolioIsReady !== false
        && !hasExpectedActualScope;
    const isActualLoading = actualPortfolioIsReady !== false && (
        isActualUnresolved
        || isActualScopePending
        || Boolean(positionsLoading)
        || Boolean(holdingsLoading)
        || Boolean(calculationsLoading)
        || Boolean(matricesLoading)
        || Boolean(coreLoadVisualLoading)
        || Boolean(betaSourceTransitionLoading)
    );
    const isActualGroupLoading = actualPortfolioIsReady !== false && (
        isActualUnresolved
        || isActualScopePending
        || Boolean(groupRisksLoading)
        || (!hasActualPortfolioPositions && (
            Boolean(positionsLoading) || Boolean(holdingsLoading)
        ))
    );

    const isConfirmedEmpty = actualPortfolioIsReady === false;
    const actualPortfolioEmpty = actualPortfolioIsReady === undefined
        ? !hasActualPortfolioPositions && !isActualLoading
        : isConfirmedEmpty;

    const actualResult = readRiskOverviewResource(
        hasExpectedActualScope ? calculations : null,
        {
            loading: hasExpectedActualScope && (
                calculationsLoading
                || matricesLoading
                || isActualUnresolved
                || Boolean(positionsLoading)
                || Boolean(holdingsLoading)
            ),
            expectedScopeKey: expectedActualScopeKey,
        },
    );
    const actualScopeMatches = hasExpectedActualScope
        && actualResult.scopeKey === expectedActualScopeKey;
    const hasActualError = actualScopeMatches
        && actualResult.state === RISK_OVERVIEW_RESOURCE_STATES.ERROR;
    const isActualResourceLoading = hasExpectedActualScope && (
        isActualLoading || actualResult.state === RISK_OVERVIEW_RESOURCE_STATES.LOADING
    );

    const actual = hasExpectedActualScope && (
        hasActualPortfolioPositions || hasActualError || isActualResourceLoading
    )
        ? actualResult
        : (isActualLoading && !isConfirmedEmpty
            ? createRiskOverviewResource({
                scopeKey: expectedActualScopeKey || '',
                state: RISK_OVERVIEW_RESOURCE_STATES.LOADING,
                data: {},
            })
            : createRiskOverviewResource({
                state: RISK_OVERVIEW_RESOURCE_STATES.EMPTY,
                data: {},
            }));

    const selected = readRiskOverviewResource(
        hasExpectedSelectedScope && hasActualPortfolioPositions && optimalPortfolioSummaryLoaded
            ? optimizedRiskMetrics
            : null,
        {
            loading: hasExpectedSelectedScope
                && hasActualPortfolioPositions
                && Boolean(optimalPortfolioSummary)
                && !optimalPortfolioSummaryLoaded,
            expectedScopeKey: expectedSelectedScopeKey,
        },
    );

    const actualGroup = hasExpectedActualScope
        && (hasActualPortfolioPositions || isActualGroupLoading)
        && !isConfirmedEmpty
        ? readRiskOverviewResource(groupRiskMetrics, {
            loading: isActualGroupLoading,
            expectedScopeKey: expectedActualScopeKey,
        })
        : (isActualGroupLoading && !isConfirmedEmpty
            ? createRiskOverviewResource({
                state: RISK_OVERVIEW_RESOURCE_STATES.LOADING,
                data: {},
            })
            : createRiskOverviewResource({
                state: RISK_OVERVIEW_RESOURCE_STATES.EMPTY,
                data: {},
            }));

    const selectedGroup = readRiskOverviewResource(
        hasExpectedSelectedScope && hasActualPortfolioPositions && optimalPortfolioSummaryLoaded
            ? optimizedGroupRiskMetrics
            : null,
        {
            loading: hasExpectedSelectedScope
                && hasActualPortfolioPositions
                && Boolean(optimalPortfolioSummary)
                && (optimizedGroupRiskMetricsLoading || !optimalPortfolioSummaryLoaded),
            expectedScopeKey: expectedSelectedScopeKey,
        },
    );

    const holdingsResource = createRiskOverviewResource({
        scopeKey: actual.scopeKey,
        generation: actual.generation,
        state: (hasActualPortfolioPositions && actualScopeMatches) || hasActualError
            ? actual.state
            : (isActualLoading && !isConfirmedEmpty
                ? RISK_OVERVIEW_RESOURCE_STATES.LOADING
                : RISK_OVERVIEW_RESOURCE_STATES.EMPTY),
        data: hasActualPortfolioPositions && actualScopeMatches ? combinedHoldings : [],
        coverage: actual.coverage,
    });

    return (
        <RiskOverview
            riskOverviewTopPanels={hasActualPortfolioPositions && actualScopeMatches
                ? riskOverviewTopPanels
                : []}
            resources={{ actual, selected, actualGroup, selectedGroup, holdings: holdingsResource }}
            selectedOptimalPortfolioName={optimalPortfolioSummary}
            selectedFactor={selectedFactor}
            handleSelectFactor={handleSelectFactor}
            sedolsForFactor={sedolsForFactor}
            tickersLoading={tickersLoading}
            tickersError={tickersError}
            industries={industries}
            watchlistAttributeSnapshot={watchlistAttributeSnapshot}
            priceSyncLoading={priceRefreshLoading}
            priceSyncControl={priceSyncControl}
            onPriceSync={onPriceSync}
            groupLoadingOverride={
                !isConfirmedEmpty
                && (hasActualPortfolioPositions || isActualLoading)
                && (betaSourceTransitionLoading || coreLoadVisualLoading)
            }
            actualPortfolioEmpty={actualPortfolioEmpty}
            riskFactorsHeaderActions={riskFactorsHeaderActions}
            aum={aum}
            aumLoading={aumLoading}
            onOpenPortfolioImports={handleOpenPortfolioImports}
            onOpenPortfolioEditor={onOpenPortfolioEditor}
            onOpenPositionSizing={onOpenPositionSizing}
            showImportGuide={showPortfolioImportGuide}
            onDismissImportGuide={onDismissPortfolioImportGuide}
        />
    );
}
